import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, PackageOpen, UserRound } from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
    paid: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    unpaid: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
};

export default function MyOrders() {
    const { user, loading, login, logout } = useAuth();
    const [orders, setOrders] = useState(null);

    useEffect(() => {
        if (!user) return;
        apiClient
            .get("/my-orders")
            .then(({ data }) => setOrders(data))
            .catch(() => setOrders([]));
    }, [user]);

    if (loading) {
        return (
            <div className="py-28 flex justify-center" data-testid="my-orders-loading">
                <Loader2 size={28} className="animate-spin text-[var(--sss-pink)]" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="my-orders-signin">
                <div className="bg-white rounded-3xl sss-shadow p-8">
                    <div className="mx-auto w-14 h-14 rounded-full bg-[var(--sss-blush)] flex items-center justify-center">
                        <UserRound size={26} className="text-[var(--sss-pink)]" strokeWidth={1.6} />
                    </div>
                    <h1 className="font-serif-display text-3xl mt-5 font-semibold">Welcome back!</h1>
                    <p className="mt-2 text-sm text-[var(--sss-text-muted)]">Sign in with Google to see your past orders and check out faster.</p>
                    <button type="button" onClick={login} className="btn-pink mt-6 w-full justify-center" data-testid="google-signin-button">
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#fff" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z" />
                        </svg>
                        Sign in with Google
                    </button>
                    <p className="mt-4 text-xs text-[var(--sss-text-muted)]">No password needed — just your Google account.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12" data-testid="my-orders-page">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    {user.picture && <img src={user.picture} alt="" className="w-11 h-11 rounded-full border-2 border-[var(--sss-border)]" referrerPolicy="no-referrer" />}
                    <div>
                        <h1 className="font-serif-display text-3xl font-bold">My Orders</h1>
                        <p className="text-sm text-[var(--sss-text-muted)]" data-testid="my-orders-email">{user.email}</p>
                    </div>
                </div>
                <button type="button" onClick={logout} className="flex items-center gap-2 text-sm font-medium text-[var(--sss-text-muted)] hover:text-[var(--sss-pink)] transition px-4 py-2 rounded-full hover:bg-[var(--sss-blush)]" data-testid="signout-button">
                    <LogOut size={16} /> Sign Out
                </button>
            </div>

            {orders === null ? (
                <div className="py-16 flex justify-center">
                    <Loader2 size={26} className="animate-spin text-[var(--sss-pink)]" />
                </div>
            ) : orders.length === 0 ? (
                <div className="mt-10 bg-white rounded-3xl sss-shadow p-10 text-center" data-testid="my-orders-empty">
                    <PackageOpen size={36} className="mx-auto text-[var(--sss-pink)]" strokeWidth={1.4} />
                    <h2 className="font-serif-display text-2xl mt-4 font-semibold">No orders yet</h2>
                    <p className="mt-2 text-sm text-[var(--sss-text-muted)]">Orders placed with {user.email} will show up here.</p>
                    <Link to="/menu" className="btn-pink mt-6" data-testid="my-orders-shop-link">Shop the Menu</Link>
                </div>
            ) : (
                <ul className="mt-8 space-y-4" data-testid="my-orders-list">
                    {orders.map((o) => (
                        <li key={o.id}>
                            <Link to={`/order-confirmation/${o.id}`} className="block bg-white rounded-3xl sss-shadow p-5 md:p-6 hover:-translate-y-0.5 transition" data-testid={`order-card-${o.id}`}>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]">
                                            {new Date(o.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · #{o.id.slice(0, 8)}
                                        </p>
                                        <p className="mt-1 text-sm font-medium">
                                            {o.items.map((it) => `${it.product_name} (${it.size} × ${it.quantity})`).join(", ")}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_STYLE[o.payment_status] || "bg-gray-100 text-gray-600"}`}>
                                            {o.payment_status === "paid" ? "Paid" : o.payment_status === "failed" ? "Failed" : "Awaiting Payment"}
                                        </span>
                                        <span className="font-serif-display text-xl font-bold">${o.subtotal.toFixed(2)}</span>
                                    </div>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
